/**
 * Header.jsx — Topo do DetailPanel.
 *
 * Conteúdo:
 *   - UUID pequeno (product.id) — só se row.productId existir.
 *   - Nome do produto em destaque (uppercase).
 *   - TagsEditor inline (auto-tags + manuais como chips, + adicionar).
 *   - Botões de navegação (◀ ▶) — só se hasNav.
 *   - Botão fechar (X) — só se onClose passado.
 *   - Linha de tabs (flex-wrap pra container estreito).
 *
 * Estado interno: nenhum aqui — TagsEditor tem o próprio state local; tab
 * ativa vem do Context.
 */
import { useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, X } from 'shared/icons';
import { useToast } from '../../../../components/ui/Toast';
import { api } from '../../../../services/api';
import { SecureMlImageUrl as secureMlImageUrl } from '../../../../utils/ml';
import { GetTagBadgeColor as getTagBadgeColor, IsSystemTag as isSystemTag } from '../../constants/columns';
import { TBadge, TBtn } from '../primitives';
import { useDetailPanel } from './index.jsx';

/**
 * Editor inline de tags. Renderiza:
 *   - TODAS as tags do produto (auto + manuais), coloridas pelo nome.
 *   - X só nas MANUAIS (auto-tags vêm do estado — só some ajustando threshold).
 *   - Botão "+ adicionar" no fim, que abre input inline.
 *
 * Usado direto no HEADER do DetailPanel — sem wrapping de label/grid.
 */
function TagsEditor({ row, T, onUpdateRow }) {
    const toast = useToast();
    const initial = Array.isArray(row.manualTags) ? row.manualTags : [];
    const [manualTags, setManualTags] = useState(initial);
    const [adding, setAdding] = useState(false);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const savedRef = useRef(initial.join('|'));

    // Reset quando o SKU muda.
    const currentKey = initial.join('|');
    if (savedRef.current !== currentKey) {
        savedRef.current = currentKey;
        setManualTags(initial);
        setAdding(false);
        setDraft('');
    }

    // `row.tags` combina auto + manuais (calculado em useOperacoesData).
    // Pra otimismo: mostramos manualTags local + auto-tags que vieram do row.
    const autoTags = (Array.isArray(row.tags) ? row.tags : []).filter((t) => !initial.includes(t));
    const displayTags = [...autoTags, ...manualTags];

    const persist = async (nextManual) => {
        setSaving(true);
        try {
            await api.updateProductTags(row.productId || row.sku, nextManual);
            savedRef.current = nextManual.join('|');
            onUpdateRow?.(row.sku, { manualTags: nextManual });
            toast.success('Tags atualizadas');
        } catch (err) {
            setManualTags(initial);
            toast.error(err?.message || 'Erro ao salvar tags');
        } finally {
            setSaving(false);
        }
    };

    const handleAdd = () => {
        const value = String(draft || '')
            .trim()
            .toLowerCase();
        if (!value) {
            setAdding(false);
            setDraft('');
            return;
        }
        if (value === 'baixo estoque') {
            toast.error('"baixo estoque" é automática — ajuste via aba Cadastro > Tag: baixo estoque');
            return;
        }
        if (manualTags.includes(value) || autoTags.includes(value)) {
            toast.info('Tag já existe nesse produto');
            return;
        }
        const next = [...manualTags, value];
        setManualTags(next);
        setAdding(false);
        setDraft('');
        persist(next);
    };

    const handleRemove = (tag) => {
        // Só remove manual — auto-tags (ex: "baixo estoque") são derivadas.
        if (!manualTags.includes(tag)) return;
        const next = manualTags.filter((t) => t !== tag);
        setManualTags(next);
        persist(next);
    };

    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 8 }}>
            {displayTags.map((tag) => {
                // System tags (sem estoque, baixo estoque, sem venda 30d,
                // outbound, reputacao*) NÃO podem ser removidas pelo user:
                //   - Auto-tags (recomputeRowTags) reaparecem no próximo
                //     render — remover é no-op.
                //   - Reputation: removidas pelo worker quando o ML não
                //     sinaliza mais o problema.
                // Mesmo critério vale pra MLB-context (worker controla).
                const removable = manualTags.includes(tag) && !isSystemTag(tag);
                return (
                    <span
                        key={tag}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 2,
                        }}
                    >
                        <TBadge color={getTagBadgeColor(tag)}>{tag}</TBadge>
                        {removable && (
                            <button
                                type="button"
                                onClick={() => handleRemove(tag)}
                                disabled={saving}
                                title="Remover tag"
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: saving ? 'not-allowed' : 'pointer',
                                    color: T.muted,
                                    opacity: 0.6,
                                    padding: 0,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    marginLeft: -2,
                                }}
                            >
                                <X size={10} />
                            </button>
                        )}
                    </span>
                );
            })}
            {adding ? (
                <input
                    type="text"
                    // biome-ignore lint/a11y/noAutofocus: foco intencional ao abrir o editor
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAdd();
                        if (e.key === 'Escape') {
                            setAdding(false);
                            setDraft('');
                        }
                    }}
                    onBlur={handleAdd}
                    placeholder="nome da tag"
                    maxLength={40}
                    style={{
                        padding: '2px 6px',
                        border: `1px solid ${T.line}`,
                        background: T.bg,
                        color: T.ink,
                        borderRadius: 3,
                        fontFamily: T.sans,
                        fontSize: 11,
                        width: 140,
                        outline: 'none',
                    }}
                />
            ) : (
                <button
                    type="button"
                    onClick={() => setAdding(true)}
                    disabled={saving}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 3,
                        padding: '2px 8px',
                        background: 'transparent',
                        color: T.muted,
                        border: `1px dashed ${T.line}`,
                        borderRadius: 3,
                        fontSize: 11,
                        fontFamily: T.sans,
                        cursor: saving ? 'not-allowed' : 'pointer',
                    }}
                >
                    + adicionar
                </button>
            )}
        </div>
    );
}

export function Header() {
    const { row, T, tab, setTab, tabs, hasNav, onPrev, onNext, onClose, onUpdateRow } = useDetailPanel();
    return (
        <div
            style={{
                padding: '14px 18px 0',
                borderBottom: `1px solid ${T.line}`,
                flexShrink: 0,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                {/* Foto do anúncio (ml_listings.thumbnail). Só renderiza quando
                    existe — sem placeholder cinza ocupando espaço à toa. */}
                {secureMlImageUrl(row.thumbnail) && (
                    <img
                        src={secureMlImageUrl(row.thumbnail)}
                        alt=""
                        width={56}
                        height={56}
                        style={{
                            width: 56,
                            height: 56,
                            objectFit: 'cover',
                            borderRadius: 4,
                            border: `1px solid ${T.line}`,
                            background: T.bgMid,
                            flexShrink: 0,
                        }}
                    />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                    {row.productId && (
                        <div style={{ fontSize: 10, fontFamily: T.mono, color: T.subtle, marginBottom: 4 }}>
                            {row.productId}
                        </div>
                    )}
                    {/* Header simplificado: só o nome (sem prefixo "Nome:"),
                        fonte maior pra dar destaque. SKU e Marca aparecem
                        na aba Cadastro — redundância removida. */}
                    <div
                        style={{
                            fontSize: 18,
                            fontWeight: 600,
                            color: T.ink,
                            lineHeight: 1.3,
                            marginBottom: 6,
                            wordBreak: 'break-word',
                            textTransform: 'uppercase',
                        }}
                    >
                        {row.name || '—'}
                    </div>
                    {/* Editor de tags no header: mostra auto-tags (ex: "baixo estoque")
                        + manuais como chips coloridos. X só nas manuais; auto-tags são
                        derivadas (ajustar threshold via aba Cadastro pra mudar a auto). */}
                    {/* `key={row.sku||row.id}` força DESMONTAR/REMONTAR o
                        TagsEditor quando o produto muda. Antes ele
                        preservava state local (manualTags) entre
                        navegações, fazendo a tag adicionada no produto
                        A aparecer transitoriamente quando o user ia pro
                        produto B. Reset via savedRef era falho. */}
                    <TagsEditor key={row.sku || row.id} row={row} T={T} onUpdateRow={onUpdateRow} />
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                    {hasNav && (
                        <>
                            <TBtn variant="ghost" size="xs" onClick={onPrev} title="Produto anterior">
                                <ArrowLeft size={12} />
                            </TBtn>
                            <TBtn variant="ghost" size="xs" onClick={onNext} title="Próximo produto">
                                <ArrowRight size={12} />
                            </TBtn>
                        </>
                    )}
                    {onClose && (
                        <TBtn variant="ghost" size="sm" onClick={onClose}>
                            <X size={14} />
                        </TBtn>
                    )}
                </div>
            </div>

            {/* Tabs com flex-wrap — em containers estreitos (~360-460px)
                Inbounds/Outbounds quebram em 2ª linha em vez de gerar
                scroll horizontal (UX melhor pra desktop denso). */}
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0,
                    marginBottom: -1,
                }}
            >
                {tabs.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => setTab(t.id)}
                        style={{
                            padding: '8px 14px',
                            fontSize: 12,
                            cursor: 'pointer',
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            borderBottom: tab === t.id ? `2px solid ${T.ink}` : '2px solid transparent',
                            fontWeight: tab === t.id ? 600 : 400,
                            color: tab === t.id ? T.ink : T.muted,
                            fontFamily: T.sans,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {t.label}
                    </button>
                ))}
            </div>
        </div>
    );
}
